<script setup lang="ts">
  import { getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';

  /**
   * «Diseño y estilo» while the store's saved look loads (skeleton cards in
   * the shape of the page's cards), or when it could not load (a message and
   * «Reintentar»). The cards never show a look that did not come from the API.
   */
  defineProps<{
    failed?: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'retry'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));
  /** One skeleton per card of the page, sized like it (style, colour, mode, cover, more). */
  const CARDS = ['style', 'color', 'mode', 'cover', 'more'];
</script>

<template>
  <div class="v2-design-look-state">
    <div v-if="failed" class="look-error" role="alert">
      <PosUiEmptyState icon="cloud-alert" :title="t('errors.tryAgain')">
        <template #action>
          <PosUiButton
            color="neutral"
            variant="outline"
            icon="refresh"
            @click="emit('retry')"
          >
            {{ t('design.retry') }}
          </PosUiButton>
        </template>
      </PosUiEmptyState>
    </div>
    <div v-else class="look-loading">
      <p class="loading-text" role="status">
        {{ t('menuPage.groups.picker.loading') }}
      </p>
      <section
        v-for="card in CARDS"
        :key="card"
        class="loading-card"
        aria-hidden="true"
      >
        <div class="card-head">
          <PosUiSkeleton class="head-title" />
          <PosUiSkeleton class="head-text" />
        </div>
        <div class="card-body">
          <PosUiSkeleton class="body-block" :class="`is-${card}`" />
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-design-look-state {
    @apply tw-pb-6;

    .loading-text {
      @apply tw-sr-only;
    }

    .look-error {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface;
    }

    .loading-card {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface;

      & + .loading-card {
        @apply tw-mt-3;
      }
    }

    .card-head {
      @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-4 md:tw-px-6;
    }

    .head-title {
      @apply tw-h-4 tw-w-28;
    }

    .head-text {
      @apply tw-mt-2 tw-h-3 tw-w-2/3;
    }

    .card-body {
      @apply tw-px-5 tw-py-5 md:tw-px-6;
    }

    .body-block {
      @apply tw-h-11 tw-w-full;

      &.is-style {
        @apply tw-h-48;
      }

      &.is-color {
        @apply tw-h-28;
      }

      &.is-cover {
        @apply tw-h-40;
      }

      &.is-more {
        @apply tw-h-20;
      }
    }
  }
</style>
